/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// MAIN OBSERVER
let main_element = document.querySelector("#main");

let observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    let oldValue = mutation.oldValue;
    let newValue = mutation.target.textContent;
    if (oldValue !== newValue) {
      initFilterCustomAvailability();
    }
  });
});
observer.observe(main_element, {
  characterDataOldValue: true,
  subtree: true,
  childList: true,
  characterData: true,
});

// LAYOUT SWITCH
const btnLayoutSwitch = document.querySelectorAll(".set-layout-switch");
let defaultLayout =
  Number(
    document.querySelector(".product-list__inner")?.getAttribute("data-layout")
  ) || 4;

if (btnLayoutSwitch) {
  btnLayoutSwitch.forEach(function (btn) {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      let layoutSwitchCount = btn.getAttribute("data-layout-switch-count");
      setLayoutProduct(layoutSwitchCount);
    });
  });
}

function setLayoutProduct(layoutSwitchCount) {
  const elProductList = document.querySelector(".product-list__inner");
  if (elProductList) {
    btnLayoutSwitch.forEach(function (btn) {
      btn.classList.remove("open");
    });

    const btn = document.querySelector(
      '.set-layout-switch[data-layout-switch-count="' + layoutSwitchCount + '"]'
    );
    if (btn) btn.classList.add("open");

    elProductList.dataset.layout = layoutSwitchCount;
    //setCookie("layout_switch_count", layoutSwitchCount, 365);
  }
}

//let layoutSwitchCount = getCookie("layout_switch_count");
//if (!layoutSwitchCount) layoutSwitchCount = defaultLayout;
//setLayoutProduct(layoutSwitchCount);

// COOKIES
function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

function getCookie(cname) {
  let name = cname + "=";
  let ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

// FIT TEXT
const elCollectionTitle = document.querySelector(
  ".shopify-section--collection-banner .image-overlay__content-wrapper h1, .shopify-section--page-banner .image-overlay__content-wrapper h1"
);
if (elCollectionTitle) {
  $(".banner-main-title h1").slabText({
    viewportBreakpoint: 400,
    maxFontSize: 80,
  });
}

// FILTER availability
function initFilterCustomAvailability() {
  let filterInputBtn = document.querySelectorAll(
    "#facet-filters .product-facet__filter-list input"
  );
  if (filterInputBtn) {
    filterInputBtn.forEach(function (input) {
      if (input.name != "filter.v.availability") {
        input.addEventListener("change", function (e) {
          e.preventDefault();
          filtersCheckTagExists();
        });
      }
    });
  }
}
initFilterCustomAvailability();

function filtersCheckTagExists() {
  let i = 0;
  let filterInputChecked = document.querySelectorAll(
    "#facet-filters .product-facet__filter-list input"
  );
  if (filterInputChecked) {
    filterInputChecked.forEach(function (input) {
      if (input.name != "filter.v.availability" && input.checked) i++;
    });
  }

  let filterAvailableInput1 = document.querySelector(
    "#product-facet-facet-filter-filter-v-availability input[value='1']"
  );
  let filterAvailableInput0 = document.querySelector(
    "#product-facet-facet-filter-filter-v-availability input[value='0']"
  );

  if (i == 0) {
    filterAvailableInput1.checked = false;
    let filterAvailableRemove = document.querySelector(
      ".product-facet__active-list .tag[data-label='filter-v-availability'] a"
    );
    if (filterAvailableRemove) filterAvailableRemove.click();
  } else filterAvailableInput1.checked = true;

  filterAvailableInput0.checked = false;
}

// ACCORDION
let accordions = document.querySelectorAll(".accordion");
if (accordions) {
  accordions.forEach(function (accordion) {
    let btn = accordion.querySelector(".accordion-title");
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      btn.parentNode.classList.toggle("active");
    });
  });
}

/**
 * COLLECTION BANNER
 */

// On loading, automatically scroll the menu to the active item
document.addEventListener("DOMContentLoaded", function () {
  document
    .querySelectorAll(".shopify-section--collection-banner .link-bar")
    .forEach((section) => {
      const targetElement = section.querySelector(
        ".link-bar__link-item--selected"
      );

      if (targetElement) {
        targetElement.scrollIntoView({ block: "center" });
      }
    });
});

/* SLIDESHOW VIDEO */
document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll(".shopify-section--slideshow")
    .forEach((section) => {
      section.querySelectorAll("slide-show").forEach((slideshow) => {
        console.log(slideshow);
        slideshow.addEventListener("page-dots:changed", () => {
          const selectedSlide = slideshow.querySelector(
            "slide-show-item:not([hidden])"
          );
          const allVideos = slideshow.querySelectorAll("native-video video");

          // Arrêter et réinitialiser toutes les vidéos d'abord
          allVideos.forEach((video) => {
            video.pause();
            video.currentTime = 0;
          });

          // Lancer la vidéo de la slide active si elle existe
          const activeVideo =
            selectedSlide?.querySelector("native-video video");
          if (activeVideo) {
            activeVideo.currentTime = 0;
            activeVideo.play();
          }
        });
      });
    });
});

/* MIX AND MATCH BUNDLE */
document.addEventListener("DOMContentLoaded", () => {
  /* VARIANT SELECTOR TEXT CLEANUP */
  // Liste des couleurs à filtrer
  const colors = [
    "Orange",
    "Bleu",
    "Rose",
    "Rouge",
    "Vert",
    "Jaune",
    "Noir",
    "Blanc",
    "Gris",
    "Violet",
    "Marron",
    "Beige",
    "Navy",
    "Bordeaux",
    "Multicolore",
    "Kaki",
    // Versions en anglais
    "Blue",
    "Pink",
    "Red",
    "Green",
    "Yellow",
    "Black",
    "White",
    "Grey",
    "Gray",
    "Purple",
    "Brown",
    "Navy Blue",
    "Burgundy",
    "Multicolor",
  ];

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Sélecteur de produits sur le côté
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "class"
      ) {
        if (
          mutation.target.classList.contains("styles_selectedOption_0f16eb5b")
        ) {
          // Récupérer le pourcentage de réduction
          const discountElement = mutation.target.querySelector(
            ".styles_option_container_0f16eb5b:nth-child(2) .styles_subtitle_0f16eb5b"
          );
          if (discountElement) {
            // Extraire uniquement le nombre suivi du % en utilisant une regex
            const discountMatch = discountElement.textContent.match(/(\d+)%/);
            const discount = discountMatch ? discountMatch[1] + "%" : "0%";

            // Mettre à jour tous les titres de variantes avec le pourcentage
            document
              .querySelectorAll(".variant_title_variant_b4dca3bb")
              .forEach((titleElement) => {
                const originalText = titleElement.textContent.split(" -")[0]; // Enlever l'ancien pourcentage s'il existe
                titleElement.innerHTML = `${originalText} <span class="mix-and-match__selected-product-discount">-${discount}</span>`;
              });
          }
        }
      }

      // Observer les nouveaux éléments ajoutés
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Chercher les éléments de réduction
          const discountElements = node.classList?.contains(
            "styles_discountOption_0f16eb5b"
          )
            ? [
                node.querySelector(
                  ".styles_option_container_0f16eb5b:nth-child(2) .styles_subtitle_0f16eb5b"
                ),
              ]
            : node.querySelectorAll(
                ".styles_discountOption_0f16eb5b .styles_option_container_0f16eb5b:nth-child(2) .styles_subtitle_0f16eb5b"
              );

          discountElements.forEach((element) => {
            if (element) {
              element.textContent = element.textContent.replace(
                "DE",
                "de réduction"
              );
            }
          });

          // Déplacement de la couleur du sélecteur à la carte produit
          const options = node.classList?.contains(
            "variantSelector_container_2af47cdb"
          )
            ? node.querySelectorAll("option")
            : node.querySelectorAll(
                ".variantSelector_container_2af47cdb option"
              );

          let color = "";

          options.forEach((option) => {
            let text = option.textContent;
            // Parcourir toutes les couleurs et les retirer du texte
            colors.forEach((_color) => {
              // Créer un pattern qui match la couleur avec ou sans slash
              const pattern = new RegExp(`\\s*/?\\s*${_color}\\s*/?\\s*`, "gi");
              if (text.includes(_color)) {
                color = _color;
              }
              text = text.replace(pattern, "");
            });
            option.textContent = text.trim();
          });

          if (color !== "") {
            // Move color to product title
            const div = document.createElement("div");
            div.innerHTML = color;
            node
              .querySelector(".mamProductItemCard_productTitle_86f1925c")
              ?.insertAdjacentElement("beforeend", div);
          }

          const productImage = node.querySelector(
            ".mamProductItemCard_productImage_86f1925c"
          );
          const productLink = node
            .querySelector(".mamProductItemCard_productTitle_86f1925c a")
            ?.getAttribute("href");

          if (productImage && productLink) {
            productImage.style.position = "relative";

            const previewIcon = document.createElement("a");
            previewIcon.href = productLink;
            previewIcon.target = "_blank";
            previewIcon.style.width = "28px";
            previewIcon.style.height = "28px";
            previewIcon.style.display = "flex";
            previewIcon.style.alignItems = "center";
            previewIcon.style.justifyContent = "center";
            previewIcon.style.background = "#1e2634";
            previewIcon.style.color = "#ffffff";
            previewIcon.style.borderRadius = "50%";
            previewIcon.style.position = "absolute";
            previewIcon.style.right = "12px";
            previewIcon.style.bottom = "12px";
            previewIcon.style.zIndex = "1";

            previewIcon.innerHTML = `
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3C4.36364 3 1.25818 5.28182 0 8.5C1.25818 11.7182 4.36364 14 8 14C11.6364 14 14.7418 11.7182 16 8.5C14.7418 5.28182 11.6364 3 8 3ZM8 12.5C5.51455 12.5 3.5 10.4855 3.5 8C3.5 5.51455 5.51455 3.5 8 3.5C10.4855 3.5 12.5 5.51455 12.5 8C12.5 10.4855 10.4855 12.5 8 12.5ZM8 5C6.34318 5 5 6.34318 5 8C5 9.65682 6.34318 11 8 11C9.65682 11 11 9.65682 11 8C11 6.34318 9.65682 5 8 5Z" fill="currentColor"/>
              </svg>
          `;
            productImage.insertAdjacentElement("beforeend", previewIcon);
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true, // Ajouter l'observation des attributs
    attributeFilter: ["class"], // Spécifier qu'on observe uniquement les changements de classe
  });
});

/* RICH TEXT READ MORE */
document.addEventListener("DOMContentLoaded", () => {
  function initRichTextReadMore() {
    document
      .querySelectorAll(".rich-text-read-more-button")
      .forEach((button) => {
        // Vérifier si le bouton a déjà un listener
        if (button.dataset.initialized === "true") {
          return;
        }

        const wrapper = button.previousElementSibling;
        const readMoreText = button.querySelector(".rich-text-read-more-text");
        const readLessText = button.querySelector(".rich-text-read-less-text");

        if (!wrapper || !readMoreText || !readLessText) {
          return;
        }

        // Obtenir la hauteur maximale depuis l'attribut data
        const maxHeight = parseInt(wrapper.dataset.maxHeight) || 200;

        // Temporairement retirer la classe collapsed pour mesurer la hauteur réelle
        const wasCollapsed = wrapper.classList.contains(
          "rich-text-content-wrapper--collapsed"
        );
        wrapper.classList.remove("rich-text-content-wrapper--collapsed");
        wrapper.style.maxHeight = "none";

        // Mesurer la hauteur réelle du contenu
        const contentHeight = wrapper.scrollHeight;

        // Restaurer l'état initial
        if (wasCollapsed) {
          wrapper.classList.add("rich-text-content-wrapper--collapsed");
          wrapper.style.maxHeight = maxHeight + "px";
        }

        // Si le contenu ne dépasse pas la hauteur max, cacher le bouton
        if (contentHeight <= maxHeight) {
          button.style.display = "none";
          wrapper.classList.remove("rich-text-content-wrapper--collapsed");
          return;
        }

        // S'assurer que le wrapper est en état collapsed initialement
        if (!wasCollapsed) {
          wrapper.classList.add("rich-text-content-wrapper--collapsed");
          wrapper.style.maxHeight = maxHeight + "px";
        }

        button.addEventListener("click", (e) => {
          e.preventDefault();
          const isExpanded = button.getAttribute("aria-expanded") === "true";

          if (isExpanded) {
            // Réduire
            wrapper.classList.add("rich-text-content-wrapper--collapsed");
            wrapper.style.maxHeight = maxHeight + "px";
            readMoreText.style.display = "inline";
            readLessText.style.display = "none";
            button.setAttribute("aria-expanded", "false");
          } else {
            // Étendre - mesurer la hauteur réelle au moment du clic
            wrapper.classList.remove("rich-text-content-wrapper--collapsed");
            wrapper.style.maxHeight = "none";
            const fullHeight = wrapper.scrollHeight;
            wrapper.style.maxHeight = fullHeight + "px";
            readMoreText.style.display = "none";
            readLessText.style.display = "inline";
            button.setAttribute("aria-expanded", "true");
          }
        });

        // Marquer comme initialisé
        button.dataset.initialized = "true";
      });
  }

  // Initialiser au chargement
  initRichTextReadMore();

  // Réinitialiser si de nouvelles sections sont ajoutées dynamiquement
  const richTextObserver = new MutationObserver(() => {
    initRichTextReadMore();
  });

  richTextObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

/* PREDICTIVE SEARCH — SUGGESTED TERMS */
document.addEventListener("click", function (e) {
  const termBtn = e.target.closest(".predictive-search__suggested-term");
  if (!termBtn) return;

  const drawer = termBtn.closest("predictive-search-drawer");
  if (!drawer) return;

  const input = drawer.querySelector('[name="q"]');
  if (!input) return;

  input.value = termBtn.dataset.term;
  input.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }));
  input.focus();
});
